import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import gsap from 'gsap';
import { SectionHeader, DataState, SkeletonGrid } from '../components/ui';
import { getLocalizedField } from '../lib/format';
import { useReveal } from '../hooks/useReveal';

const NEWS_GRID = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';

// Format date helper
const formatDate = (dateStr, lang) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Animated News Card Component
function NewsCard({ post, index, lang }) {
  const { t } = useTranslation();
  const cardRef = useReveal({ index, y: 60, scale: 1 });

  const title = getLocalizedField(post, 'title', lang);
  const summary = getLocalizedField(post, 'summary', lang);

  return (
    <Link
      ref={cardRef}
      to={`/news/${post.slug}`}
      className="group bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2"
    >
      {/* Image */}
      <div className="aspect-[16/9] overflow-hidden bg-gray-100 relative">
        {post.featured_image_url ? (
          <img
            src={post.featured_image_url}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
        )}

        {/* Category Badge */}
        {post.category && (
          <div className="absolute top-4 left-4">
            <span
              className="px-3 py-1 text-sm font-medium rounded-full backdrop-blur-sm"
              style={{
                backgroundColor: `${post.category.color}dd`,
                color: 'white',
              }}
            >
              {getLocalizedField(post.category, 'name', lang)}
            </span>
          </div>
        )}

        {/* Featured Badge */}
        {post.is_featured && (
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-yellow-500 text-white">
              {t('news.featured')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Date */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {formatDate(post.published_at, lang)}
        </div>

        {/* Title */}
        <h3 className="font-display font-bold text-xl text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>

        {/* Summary */}
        {summary && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-4">{summary}</p>
        )}

        {/* Read More */}
        <span className="inline-flex items-center gap-2 text-primary-600 font-medium text-sm group-hover:gap-3 transition-all">
          {t('news.readMore')}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

// Featured Article Hero
function FeaturedArticle({ post, lang }) {
  const { t } = useTranslation();
  const heroRef = useRef(null);

  useEffect(() => {
    if (!heroRef.current) return;

    gsap.fromTo(
      heroRef.current,
      { opacity: 0, scale: 0.95 },
      { opacity: 1, scale: 1, duration: 1, ease: 'power3.out' }
    );
  }, []);

  const title = getLocalizedField(post, 'title', lang);
  const summary = getLocalizedField(post, 'summary', lang);

  return (
    <Link
      ref={heroRef}
      to={`/news/${post.slug}`}
      className="group block relative rounded-3xl overflow-hidden mb-12"
    >
      {/* Background Image */}
      <div className="aspect-[21/9] md:aspect-[3/1]">
        {post.featured_image_url ? (
          <img
            src={post.featured_image_url}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700" />
        )}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
        <div className="max-w-3xl">
          {post.category && (
            <span
              className="inline-block px-4 py-1.5 text-sm font-medium rounded-full mb-4"
              style={{
                backgroundColor: post.category.color,
                color: 'white',
              }}
            >
              {getLocalizedField(post.category, 'name', lang)}
            </span>
          )}

          <h2 className="text-2xl md:text-4xl font-display font-bold text-white mb-3 group-hover:text-primary-200 transition-colors">
            {title}
          </h2>

          {summary && (
            <p className="text-white/80 text-lg line-clamp-2 mb-4 hidden md:block">
              {summary}
            </p>
          )}

          <div className="flex items-center gap-4 text-white/70 text-sm">
            <span>{formatDate(post.published_at, lang)}</span>
            <span className="inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              {t('news.readMore')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Category Filter with animation
function CategoryFilter({ categories, selected, onSelect, lang }) {
  const { t } = useTranslation();
  const filterRef = useRef(null);

  useEffect(() => {
    if (!filterRef.current) return;

    gsap.fromTo(
      filterRef.current.children,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.05, ease: 'power2.out' }
    );
  }, []);

  return (
    <div ref={filterRef} className="flex flex-wrap justify-center gap-2 mb-10">
      <button
        onClick={() => onSelect('all')}
        className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
          selected === 'all'
            ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30 scale-105'
            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
        }`}
      >
        {t('common.all')}
      </button>
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => onSelect(category.id)}
          className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
            selected === category.id
              ? 'text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
          style={selected === category.id ? {
            backgroundColor: category.color,
            boxShadow: `0 10px 15px -3px ${category.color}40`,
          } : {}}
        >
          {getLocalizedField(category, 'name', lang)}
        </button>
      ))}
    </div>
  );
}

export default function News() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const headerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Animate header
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power3.out' }
      );
    }
  }, [loading]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        supabase
          .from('website_news_articles')
          .select(`
            *,
            category:website_news_categories(id, name_en, name_id, slug, color)
          `)
          .eq('status', 'published')
          .order('is_featured', { ascending: false })
          .order('published_at', { ascending: false }),
        supabase
          .from('website_news_categories')
          .select('*')
          .eq('is_active', true)
          .order('sort_order'),
      ]);

      if (articlesRes.error) throw articlesRes.error;
      if (categoriesRes.error) throw categoriesRes.error;

      setArticles(articlesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Get featured article (first featured one)
  const featuredArticle = articles.find(a => a.is_featured);

  // Filter articles by category (excluding featured for the grid if it's shown as hero)
  const filteredArticles = articles.filter(a => {
    if (selectedCategory !== 'all' && a.category_id !== selectedCategory) return false;
    // If showing featured hero, exclude it from grid
    if (featuredArticle && a.id === featuredArticle.id && selectedCategory === 'all') return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={headerRef} className="mb-10">
          <SectionHeader title={t('news.title')} subtitle={t('news.subtitle')} />
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <CategoryFilter
            categories={categories}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
            lang={lang}
          />
        )}

        {/* Featured Article Hero (only when showing all) */}
        {featuredArticle && selectedCategory === 'all' && (
          <FeaturedArticle post={featuredArticle} lang={lang} />
        )}

        {/* Articles Grid */}
        <DataState
          loading={loading}
          error={error}
          empty={filteredArticles.length === 0}
          onRetry={fetchData}
          skeleton={<SkeletonGrid count={6} className={NEWS_GRID} />}
          emptyProps={{
            icon: '📰',
            title: t('news.noNews'),
            message: selectedCategory !== 'all' ? t('news.noCategoryArticles') : t('news.checkBack'),
          }}
        >
          <div className={NEWS_GRID}>
            {filteredArticles.map((post, index) => (
              <NewsCard key={post.id} post={post} index={index} lang={lang} />
            ))}
          </div>
        </DataState>
      </div>
    </div>
  );
}
